/**
 * DCMS Scene Loader — animated loading overlay for long AJAX operations (1-4 s).
 *
 * Public API (attached as DCMS2.loader):
 *   DCMS2.loader.show({ scene, variant, text })  or  DCMS2.loader.show('folder')
 *   DCMS2.loader.update({ text, progress })
 *   DCMS2.loader.timer('up')            — elapsed time counter
 *   DCMS2.loader.timer('down', 10)      — countdown, syncs the progressbar
 *                                         and auto-hides the overlay at 0
 *   DCMS2.loader.steps([...labels])     — step list (first becomes current)
 *   DCMS2.loader.stepDone(i)            — marks steps 0..i done, advances
 *                                         text + progress automatically
 *   DCMS2.loader.hide()
 *   DCMS2.loader.configure({ scene, variant, text, showDelayMs, minShowMs })
 *
 * Scenes:   folder | pen | packets | blocks | scan | cloud
 * Variants: icon | icon-text | icon-text-bar | icon-bar
 *
 * Anti-flicker: the overlay is painted after `showDelayMs` (default 200 ms —
 * fast responses never flash it) and, once visible, stays for at least
 * `minShowMs` (default 450 ms). All styling lives in main.css
 * (.dcms-scene-loader / .dcms-scl-*) — the overlay may be created in the TOP
 * document by an iframe page, so template-local styles would not apply.
 *
 * @author AMPD Sp. z o.o.
 * @version 1.0
 */

const SCENES = {
    folder:
        '<div class="dcms-scl-scene dcms-scl-scene-folder">'
        + '<div class="dcms-scl-folder-page dcms-scl-folder-page1"></div>'
        + '<div class="dcms-scl-folder-page dcms-scl-folder-page2">'
        + '<div class="dcms-scl-ln"></div><div class="dcms-scl-ln"></div><div class="dcms-scl-ln"></div>'
        + '</div>'
        + '<div class="dcms-scl-folder-fly">'
        + '<div class="dcms-scl-ln"></div><div class="dcms-scl-ln"></div><div class="dcms-scl-ln"></div>'
        + '</div>'
        + '<div class="dcms-scl-folder-target">'
        + '<div class="dcms-scl-folder-top"></div>'
        + '<div class="dcms-scl-folder-body"><span class="material-symbols-rounded">description</span></div>'
        + '</div>'
        + '</div>',
    pen:
        '<div class="dcms-scl-scene dcms-scl-scene-pen">'
        + '<div class="dcms-scl-pen-lines">'
        + '<div class="dcms-scl-ln"></div><div class="dcms-scl-ln"></div><div class="dcms-scl-ln"></div>'
        + '</div>'
        + '<span class="material-symbols-rounded">stylus_note</span>'
        + '</div>',
    packets:
        '<div class="dcms-scl-scene dcms-scl-scene-packets">'
        + '<div class="dcms-scl-packets-server">'
        + '<div class="dcms-scl-ln"></div><div class="dcms-scl-ln"></div><div class="dcms-scl-dot"></div>'
        + '</div>'
        + '<div class="dcms-scl-packets-wire">'
        + '<div class="dcms-scl-packets-rail"></div><div class="dcms-scl-packets-rail"></div>'
        + '<div class="dcms-scl-packet dcms-scl-packet-1"></div>'
        + '<div class="dcms-scl-packet dcms-scl-packet-2"></div>'
        + '<div class="dcms-scl-packet dcms-scl-packet-3"></div>'
        + '</div>'
        + '<div class="dcms-scl-packets-browser">'
        + '<div class="dcms-scl-packets-browser-bar"><div class="dcms-scl-dot"></div><div class="dcms-scl-dot"></div></div>'
        + '<div class="dcms-scl-packets-browser-body"><div class="dcms-scl-ln"></div><div class="dcms-scl-ln"></div></div>'
        + '</div>'
        + '</div>',
    blocks:
        '<div class="dcms-scl-scene dcms-scl-scene-blocks">'
        + '<div class="dcms-scl-blocks-header"></div>'
        + '<div class="dcms-scl-blocks-row">'
        + '<div class="dcms-scl-blocks-image"><span class="material-symbols-rounded">image</span></div>'
        + '<div class="dcms-scl-blocks-text"><div class="dcms-scl-ln"></div><div class="dcms-scl-ln"></div><div class="dcms-scl-ln"></div></div>'
        + '</div>'
        + '</div>',
    scan:
        '<div class="dcms-scl-scene dcms-scl-scene-scan">'
        + '<div class="dcms-scl-scan-lines">'
        + '<div class="dcms-scl-ln"></div><div class="dcms-scl-ln"></div><div class="dcms-scl-ln"></div><div class="dcms-scl-ln"></div>'
        + '</div>'
        + '<span class="material-symbols-rounded">search</span>'
        + '</div>',
    cloud:
        '<div class="dcms-scl-scene dcms-scl-scene-cloud">'
        + '<span class="material-symbols-rounded dcms-scl-cloud-icon">cloud</span>'
        + '<div class="dcms-scl-cloud-rail dcms-scl-cloud-rail-1"></div>'
        + '<div class="dcms-scl-cloud-rail dcms-scl-cloud-rail-2"></div>'
        + '<div class="dcms-scl-cloud-dot dcms-scl-cloud-dot-1"></div>'
        + '<div class="dcms-scl-cloud-dot dcms-scl-cloud-dot-2"></div>'
        + '<div class="dcms-scl-cloud-dot dcms-scl-cloud-dot-3"></div>'
        + '<div class="dcms-scl-cloud-doc"><div class="dcms-scl-ln"></div><div class="dcms-scl-ln"></div></div>'
        + '</div>'
};

const VARIANTS = ['icon', 'icon-text', 'icon-text-bar', 'icon-bar'];

class DCMSSceneLoader {

    constructor() {
        this.defaults = {
            scene: 'folder',
            variant: 'icon-text',
            text: null,
            showDelayMs: 200,
            minShowMs: 450
        };
        // volatile state of the current show()..hide() cycle
        this.state = null;
        this.showTimer = null;
        this.hideTimer = null;
        this.removeTimer = null;
        this.intervalId = null;
        this.overlay = null;
        this.doc = null;
    }

    /**
     * Overrides the page-level defaults (used by the PHP Loader component).
     */
    configure(options) {
        Object.assign(this.defaults, options || {});
    }

    /**
     * Shows the overlay. Accepts an options object or a scene name string.
     * Calling show() again while visible just applies the new options.
     *
     * @param {Object|string} [options] { scene, variant, text, showDelayMs, minShowMs }
     */
    show(options) {
        if (typeof options === 'string') {
            options = { scene: options };
        }
        options = Object.assign({}, this.defaults, options || {});
        if (!SCENES[options.scene]) {
            options.scene = this.defaults.scene;
        }
        if (VARIANTS.indexOf(options.variant) === -1) {
            options.variant = this.defaults.variant;
        }

        this._clearTimers(['hideTimer', 'removeTimer']);
        this._stopInterval();

        if (this.state && this.overlay) {
            // already pending/visible — re-render with the new options
            this.state.options = options;
            this.state.progress = 0;
            this.state.steps = null;
            this.state.timerLabel = '';
            this._render();
            return this;
        }

        this.state = {
            options: options,
            progress: 0,
            steps: null,
            timerLabel: '',
            shownAt: 0
        };
        this._mount();
        this._render();

        var self = this;
        var delay = Math.max(0, options.showDelayMs | 0);
        this.showTimer = setTimeout(function () {
            self.showTimer = null;
            if (!self.state || !self.overlay) return;
            self.state.shownAt = Date.now();
            self.overlay.classList.add('dcms-scl-open');
        }, delay);
        return this;
    }

    /**
     * Updates the text and/or the progressbar (0-100).
     */
    update(options) {
        if (!this.state) return this;
        options = options || {};
        if (typeof options.text === 'string') {
            this.state.options.text = options.text;
        }
        if (typeof options.progress === 'number') {
            this.state.progress = Math.max(0, Math.min(100, options.progress));
        }
        this._render();
        return this;
    }

    /**
     * Starts an elapsed-time counter ('up') or a countdown ('down', seconds).
     * The countdown also drives the progressbar. Pass no arguments to stop.
     */
    timer(direction, seconds) {
        if (!this.state) return this;
        this._stopInterval();
        if (!direction) {
            this.state.timerLabel = '';
            this._render();
            return this;
        }
        var self = this;
        if (direction === 'down') {
            var total = Math.max(0.1, Number(seconds) || 10);
            var end = Date.now() + total * 1000;
            this.state.timerLabel = total.toFixed(1) + ' s';
            this.state.progress = 0;
            this._render();
            this.intervalId = setInterval(function () {
                if (!self.state) return self._stopInterval();
                var remaining = Math.max(0, end - Date.now()) / 1000;
                self.state.timerLabel = remaining.toFixed(1) + ' s';
                self.state.progress = (total - remaining) / total * 100;
                self._render();
                if (remaining <= 0) {
                    // The countdown represents the remaining time of the
                    // operation — when it runs out, close the overlay instead
                    // of leaving it open forever.
                    self._stopInterval();
                    self.hide();
                }
            }, 100);
        } else {
            var start = Date.now();
            this.state.timerLabel = '0.0 s';
            this._render();
            this.intervalId = setInterval(function () {
                if (!self.state) return self._stopInterval();
                self.state.timerLabel = ((Date.now() - start) / 1000).toFixed(1) + ' s';
                self._render();
            }, 100);
        }
        return this;
    }

    /**
     * Sets the step list. The first step becomes current; text and
     * progressbar are driven automatically from now on.
     *
     * @param {string[]} labels
     */
    steps(labels) {
        if (!this.state || !Array.isArray(labels) || !labels.length) return this;
        this.state.steps = { labels: labels.slice(), current: 0 };
        this.state.progress = 0;
        this.state.options.text = labels[0] + '…';
        this._render();
        return this;
    }

    /**
     * Marks steps 0..index as done and advances to the next one,
     * updating the text and the progressbar.
     *
     * @param {number} index zero-based index of the finished step
     */
    stepDone(index) {
        var st = this.state && this.state.steps;
        if (!st) return this;
        var done = Math.max(0, Math.min(st.labels.length, (index | 0) + 1));
        st.current = done;
        this.state.progress = done / st.labels.length * 100;
        this.state.options.text = done >= st.labels.length
            ? this._t('loaderDoneText', 'Done')
            : st.labels[done] + '…';
        this._render();
        return this;
    }

    /**
     * Hides the overlay, honouring the minimum display time.
     */
    hide() {
        if (!this.state) return this;
        this._stopInterval();
        var self = this;
        var finish = function () {
            self._clearTimers(['showTimer', 'hideTimer']);
            self.state = null;
            if (self.overlay) {
                self.overlay.classList.remove('dcms-scl-open');
                self.removeTimer = setTimeout(function () {
                    self.removeTimer = null;
                    self._unmount();
                }, 250);
            } else {
                self._unmount();
            }
        };
        if (!this.state.shownAt) {
            // not painted yet (anti-flicker window) — never flash it
            finish();
            return this;
        }
        var left = Math.max(0, (this.state.options.minShowMs | 0) - (Date.now() - this.state.shownAt));
        if (left > 0) {
            this.hideTimer = setTimeout(finish, left);
        } else {
            finish();
        }
        return this;
    }

    /** True when the overlay is pending or visible. */
    isOpen() {
        return !!this.state;
    }

    // ── internals ────────────────────────────────────────────────────────────

    _t(key, fallback) {
        if (typeof window.__i18n === 'function') {
            return window.__i18n(key, 'UIBuilder', fallback);
        }
        return fallback;
    }

    /**
     * The loader covers the whole screen, so it is mounted in the highest
     * same-origin document (an iframe page paints it over the full shell).
     */
    _targetDocument() {
        var win = window;
        try {
            while (win.parent && win.parent !== win && win.parent.document) {
                win = win.parent;
            }
        } catch (e) {
            // cross-origin parent — stay in the current window
        }
        return win.document;
    }

    _mount() {
        this.doc = this._targetDocument();
        var existing = this.doc.getElementById('dcmsSceneLoader');
        if (existing && existing.parentNode) {
            existing.parentNode.removeChild(existing);
        }
        this.overlay = this.doc.createElement('div');
        this.overlay.id = 'dcmsSceneLoader';
        this.overlay.className = 'dcms-scene-loader';
        this.overlay.setAttribute('role', 'status');
        this.overlay.setAttribute('aria-live', 'polite');
        this.overlay.innerHTML =
            '<div class="dcms-scene-loader-card">'
            + '<div class="dcms-scl-scene-slot"></div>'
            + '<div class="dcms-scl-info">'
            + '<div class="dcms-scl-text dcms-scl-hidden"></div>'
            + '<div class="dcms-scl-timer dcms-scl-hidden"></div>'
            + '<div class="dcms-scl-bar dcms-scl-hidden"><div class="dcms-scl-bar-fill"></div></div>'
            + '<div class="dcms-scl-steps dcms-scl-hidden"></div>'
            + '</div>'
            + '</div>';
        this.doc.body.appendChild(this.overlay);
    }

    _unmount() {
        if (this.overlay && this.overlay.parentNode) {
            this.overlay.parentNode.removeChild(this.overlay);
        }
        this.overlay = null;
        this.doc = null;
    }

    _render() {
        if (!this.overlay || !this.state) return;
        var opts = this.state.options;
        var slot = this.overlay.querySelector('.dcms-scl-scene-slot');
        if (slot.getAttribute('data-scene') !== opts.scene) {
            slot.setAttribute('data-scene', opts.scene);
            slot.innerHTML = SCENES[opts.scene];
        }

        var showText = opts.variant.indexOf('text') !== -1 && !!opts.text;
        var showBar = opts.variant.indexOf('bar') !== -1;
        var showTimer = !!this.state.timerLabel;
        var showSteps = !!this.state.steps;

        var textEl = this.overlay.querySelector('.dcms-scl-text');
        textEl.classList.toggle('dcms-scl-hidden', !showText);
        if (showText) textEl.textContent = opts.text;

        var timerEl = this.overlay.querySelector('.dcms-scl-timer');
        timerEl.classList.toggle('dcms-scl-hidden', !showTimer);
        if (showTimer) timerEl.textContent = this.state.timerLabel;

        var barEl = this.overlay.querySelector('.dcms-scl-bar');
        barEl.classList.toggle('dcms-scl-hidden', !showBar);
        if (showBar) {
            this.overlay.querySelector('.dcms-scl-bar-fill').style.transform =
                'scaleX(' + (this.state.progress / 100) + ')';
        }

        var stepsEl = this.overlay.querySelector('.dcms-scl-steps');
        stepsEl.classList.toggle('dcms-scl-hidden', !showSteps);
        if (showSteps) {
            this._renderSteps(stepsEl);
        } else {
            stepsEl.textContent = '';
        }
    }

    _renderSteps(container) {
        var st = this.state.steps;
        container.textContent = '';
        for (var i = 0; i < st.labels.length; i++) {
            var status = i < st.current ? 'done' : (i === st.current ? 'current' : 'pending');
            var row = this.doc.createElement('div');
            row.className = 'dcms-scl-step dcms-scl-step-' + status;
            var icon = this.doc.createElement('span');
            icon.className = 'material-symbols-rounded dcms-scl-step-icon';
            icon.textContent = status === 'done'
                ? 'check_circle'
                : (status === 'current' ? 'progress_activity' : 'radio_button_unchecked');
            var label = this.doc.createElement('span');
            label.className = 'dcms-scl-step-label';
            label.textContent = st.labels[i];
            row.appendChild(icon);
            row.appendChild(label);
            container.appendChild(row);
        }
    }

    _clearTimers(keys) {
        var self = this;
        keys.forEach(function (key) {
            if (self[key]) {
                clearTimeout(self[key]);
                self[key] = null;
            }
        });
    }

    _stopInterval() {
        if (this.intervalId) {
            clearInterval(this.intervalId);
            this.intervalId = null;
        }
    }
}

// Singleton attached to the DCMS2 namespace (created here if main.js has not
// run yet — APP's constructor merges into an existing namespace).
window.DCMS2 = window.DCMS2 || {};
if (!window.DCMS2.loader) {
    window.DCMS2.loader = new DCMSSceneLoader();
}

export { DCMSSceneLoader };
